<!-- -->
<!-- Copyright (c) 2025 Cozy Intellect Inc.-->
<!-- File Path: apps/web/src/routes/+layout.svelte-->
<!-- -->

<script lang="ts">
	import { browser } from '$app/environment';
	import { page } from '$app/stores';
	import {
		DEFAULT_POST_SIGN_IN_ROUTE,
		signInWithCallbackUrl,
		signOut,
		signUpWithCallbackUrl
	} from '$lib/client-util/client-auth.util';
	import DownloadDesktopAppModal from '$lib/components/DownloadDesktopAppModal.svelte';
	import { downloadModalStore } from '$lib/downloadModal.store';
	import { GoogleAnalytics } from '@beyonk/svelte-google-analytics';
	import '@fontsource/open-sans-condensed';
	import type { User, UserAuth } from '@repo/common';
	import LandingFooter from '@repo/landing/src/components/Footer.svelte';
	import LandingHeader from '@repo/landing/src/components/Header.svelte';
	import { setHandler } from '@repo/sharedlib';
	import { invalidate } from '@repo/sharedlib/src/lib/invalidate.util';
	import { setUser, setUserAuth } from '@repo/sharedlib/src/stores/auth.store';
	import constants from '@repo/sharedlib/src/utils/constants';
	import { isMobileWeb } from '@repo/sharedlib/src/stores/layout.store';
	import * as MainPanes from '@repo/ui/src/components/MainPanes/index';
	import NavigationProgress from '@repo/ui/src/components/NavigationProgress.svelte';
	import ProtectedPage from '@repo/ui/src/components/ProtectedPage.svelte';
	import * as Tooltip from '@repo/ui/src/components/ui/tooltip/index';
	import BaseMenuContext from '@repo/ui/src/components/context-menu/BaseMenuContext.svelte';
	import ExportToLinearDialog from '@repo/ui/src/components/Dialogs/ExportToLinearDialog.svelte';
	import { Toaster } from '@repo/ui/src/lib/toast';
	import DebugPanel from '@ui-components/Debug/DebugPanel.svelte';
	import {
		closeExportToLinearDialog,
		exportLinearDialogStore
	} from '@repo/sharedlib/src/stores/exportLinearDialog.store';
	import { onMount, setContext } from 'svelte';
	import { get } from 'svelte/store';
	import '../styles/ui.css';
	import '../../../../packages/ui/src/ui.css';
	// Main tailwind entrypoint
	import '../styles/app.css'; // App-specific overrides
	// --- Metadata imports ---
	import {
		generateBlogListingMetadata,
		generateBlogPostMetadata,
		generateHelpArticleMetadata,
		generateHelpListingMetadata,
		generateMetaTags
	} from '$lib/blog-lib/utils/metadata';
	import type { BrowserLib } from '@repo/browserlib';
	import { loadConfig } from '@repo/sharedlib/src/stores/config';
	import { FEATURE_FLAGS } from '@repo/sharedlib/src/utils/constants';
	import { getHandler } from '$lib/handler';
	interface Props {
		children?: import('svelte').Snippet;
	}

	let { children }: Props = $props();

	let isLoading = $state(true);
	// Track mount state to avoid hydration issues with browser-only components
	let mounted = $state(false);

	// Initialize pathname for SSR
	let pathname: string = $derived($page.url.pathname || '');

	// Determine if we should show the main panes based on the route
	let shouldShowMainPanes = $derived(
		pathname.startsWith('/account') ||
			pathname.startsWith('/protected') ||
			pathname.startsWith('/calendar') ||
			pathname.startsWith('/profile')
	);

	// Mobile detection for sheet layout
	const isMobile = constants.PLATFORM === 'ios' || $isMobileWeb;

	let noHeaderBaseRoutes = $derived(['/app']);
	let noFooterBaseRoutes = $derived(['/app', '/account', '/help']);

	let shouldShowHeader = $derived(
		!['sign-in', 'sign-up', '404', 'checkout', 'download', 'success', 'completeSignIn'].includes(
			pathname.split('/').pop() || ''
		) && !noHeaderBaseRoutes.some((route) => pathname.startsWith(route))
	);

	$effect(() => {
		console.log('shouldShowHeader', shouldShowHeader);
		console.log('pathname', pathname);
	});

	let shouldShowFooter = $derived(
		!['sign-in', 'sign-up', '404', 'checkout', 'download', 'success', 'completeSignIn'].includes(
			pathname.split('/').pop() || ''
		) && !noFooterBaseRoutes.some((route) => pathname.startsWith(route))
	);

	// --- Centralized metadata logic ---
	let metaTags = $derived(
		(() => {
			const baseUrl = 'https://retainapp.ai';
			const currentPathname = $page.url.pathname;

			if (currentPathname.startsWith('/blog/post/') && $page.data.post) {
				const postData = $page.data.post.data;
				if (postData) {
					const metadata = generateBlogPostMetadata(postData, baseUrl);
					return generateMetaTags(metadata);
				}
			} else if (currentPathname.startsWith('/help/article/') && $page.data.article) {
				const articleData = $page.data.article.data;
				if (articleData) {
					const metadata = generateHelpArticleMetadata(articleData, baseUrl);
					return generateMetaTags(metadata);
				}
			} else if (currentPathname.startsWith('/blog')) {
				return generateBlogListingMetadata(baseUrl);
			} else if (currentPathname.startsWith('/help')) {
				return generateHelpListingMetadata(baseUrl);
			}

			// Default metadata fallback
			return {
				title: 'Retain AI - The only productivity app you will ever need',
				description:
					'Retain AI is the ultimate productivity app that helps you organize, remember, and act on your information. Your second brain for seamless productivity.',
				keywords: 'productivity, AI, second brain, organization, memory, task management',
				author: 'Retain AI',
				'og:type': 'website',
				'og:url': baseUrl + '/',
				'og:title': 'Retain AI - The only productivity app you will ever need',
				'og:description':
					'Retain AI is the ultimate productivity app that helps you organize, remember, and act on your information. Your second brain for seamless productivity.',
				'og:image': baseUrl + '/og-image.png',
				'og:image:width': '1200',
				'og:image:height': '630',
				'og:site_name': 'Retain AI',
				'twitter:card': 'summary_large_image',
				'twitter:url': baseUrl + '/',
				'twitter:title': 'Retain AI - The only productivity app you will ever need',
				'twitter:description':
					'Retain AI is the ultimate productivity app that helps you organize, remember, and act on your information. Your second brain for seamless productivity.',
				'twitter:image': baseUrl + '/og-image.png'
			};
		})()
	);

	const invalidateUserData = async (): Promise<void> => {
		await invalidate('user:notifications');
		await invalidate('user:calendars');
		await invalidate('user:subscription');
		await invalidate('user:cloud');
	};

	type PageSnapshot = Parameters<Parameters<typeof page.subscribe>[0]>[0];

	let hasTriggeredSignOut = false;
	let hasPromptedSignIn = false;
	let lastSyncedPageData: string | null = null;

	const syncAuthFromPage = async ($pageValue: PageSnapshot) => {
		const pageData = ($pageValue?.data ?? {}) as {
			tokenData?: UserAuth;
			dbUser?: Record<string, unknown>;
			proxyCredentials?: Record<string, string>;
			needsSignOut?: boolean;
			session?: unknown;
		};

		// Create a hash of the relevant data to detect changes
		const dataHash = JSON.stringify({
			tokenData: pageData.tokenData,
			dbUser: pageData.dbUser,
			proxyCredentials: pageData.proxyCredentials,
			needsSignOut: pageData.needsSignOut
		});

		// Skip if we've already processed this exact data
		if (lastSyncedPageData === dataHash) {
			console.log('[syncAuthFromPage] Data unchanged, skipping');
			return;
		}

		console.log('[syncAuthFromPage] Processing new auth data');
		lastSyncedPageData = dataHash;

		const { tokenData, dbUser, proxyCredentials, needsSignOut, session } = pageData;
		if (tokenData) {
			await setUserAuth(tokenData, true, 'loaded', false);
		}

		if (dbUser) {
			const updatedUser = {
				...dbUser,
				...(tokenData?.idToken ? { idToken: tokenData.idToken } : {}),
				...(proxyCredentials ? { apiToken: proxyCredentials[constants.PROXY_MODEL] } : {}),
				...(proxyCredentials ? { apiTokenM: proxyCredentials[constants.PROXY_MODEL_MINI] } : {})
			};
			console.log('[syncAuthFromPage] Setting user:', updatedUser);

			await setUser(updatedUser as Partial<User>, true, 'loaded', false);
		}

		if (!hasTriggeredSignOut && needsSignOut && session) {
			hasTriggeredSignOut = true;
			console.log('Signing out user');
			signOut();
		}
	};

	onMount(() => {
		mounted = true;
		if (!browser) return;
		isLoading = true;

		const handlePageChange = async ($pageValue: PageSnapshot) => {
			pathname = $pageValue.url.pathname || '';
			await syncAuthFromPage($pageValue);
		};

		const unsubscribe = page.subscribe(handlePageChange);

		let handler: BrowserLib;

		// Only do full BrowserLib initialization (with database/OPFS) on /app routes
		// Marketing pages don't need it and OPFS requires COOP/COEP headers
		const currentPath = get(page)?.url?.pathname ?? '';
		const isAppRoute = currentPath.startsWith('/app');

		void (async () => {
			// Skip heavy initialization on non-app routes (marketing pages)
			if (!isAppRoute) {
				// Just load config for theme settings on marketing pages
				await loadConfig();
				isLoading = false;
				return;
			}

			// Initialize the sharedlib handler for the browser first
			try {
				handler = await getHandler();
				await handler.initialize();
				// Initialize chatService after handler is set
				const { initializeChatService } = await import('@repo/sharedlib/src/chat-service-client');
				const chatService = await initializeChatService(handler);
				handler.setChatService(chatService);
				console.info('Handler initialized');
				setHandler(handler);
				const { WebRecordingController } = await import('$lib/actions/recording.actions');
				handler.recordingController = new WebRecordingController();

				// Load config from localStorage
				await loadConfig();
			} catch (error) {
				console.warn('Error initializing handler, retrying', error);
				return;
			}

			// Now sync auth from page data and wait for it to complete
			await handlePageChange(get(page));

			try {
				// Use DataPipe to handle auth → user loading
				await handler.dataPipe.user();
			} catch (error) {
				console.warn('Error loading user via DataPipe:', error);

				if (!hasPromptedSignIn) {
					const currentPage = get(page);
					const currentPathname = currentPage?.url?.pathname ?? '';
					const isProtectedRoute = currentPathname.startsWith('/app');

					if (isProtectedRoute) {
						hasPromptedSignIn = true;
						const redirectTarget = (() => {
							if (!currentPage?.url) {
								return DEFAULT_POST_SIGN_IN_ROUTE;
							}

							const { pathname, search, hash } = currentPage.url;
							const pathWithQuery = `${pathname}${search}${hash ?? ''}`;
							return pathWithQuery &&
								pathWithQuery !== '/' &&
								pathWithQuery !== '/app' &&
								pathWithQuery !== '/app/'
								? pathWithQuery
								: DEFAULT_POST_SIGN_IN_ROUTE;
						})();

						void signInWithCallbackUrl(redirectTarget);
					}
				}
			}

			try {
				// Expose browserlib to window for testing purposes
				(window as any).browserlib = handler;

				// Initialize database with loading toast (only if not already loaded)
				// Check if database models are already cached
				if (handler.isDatabaseLoaded()) {
					console.log('Database already initialized, skipping toast');
				}

				await invalidateUserData();
			} catch (error) {
				console.debug('Error initializing handler:', error);
			}

			isLoading = false;
		})();

		return () => {
			unsubscribe();
		};
	});
</script>

<svelte:head>
	<!-- Inline script to prevent FOUC (flash of unstyled content) by applying theme before render -->
	{@html `<script>
		(function() {
			try {
				// Color mappings for light and dark themes
				const colorMappings = {
					'--body-bg': { light: 'rgba(243, 244, 246, 0.5)', dark: 'rgba(9, 10, 11, 0.5)' },
					'--color-pnl': { light: '#fafafa', dark: '#090A0B' },
					'--color-pnl-secondary': { light: '#f5f5f5', dark: '#121417' },
					'--color-pnl-secondaryh': { light: '#e5e7eb', dark: '#1B1E22' },
					'--color-lbl': { light: '#404040', dark: '#fafafa' },
					'--color-lbl-secondary': { light: '#404040', dark: '#fafafa' },
					'--color-lbl-dark': { light: '#fafafa', dark: '#404040' },
					'--color-btn-primary': { light: '#64a7fa', dark: '#64a7fa' },
					'--color-btn-primaryh': { light: '#458de6', dark: '#458de6' },
					'--color-btn-primaryd': { light: '#347deb', dark: '#347deb' },
					'--color-btn-secondary': { light: '#fafafa', dark: '#121417' },
					'--color-btnlbl-primary': { light: 'white', dark: 'white' },
					'--color-btnlbl-primaryh': { light: 'white', dark: 'white' },
					'--color-acnt': { light: '#64a7fa', dark: '#47A9FF' },
					'--color-acnt-light': { light: '#458de6', dark: '#64a7fa' },
					'--color-acnt-dark': { light: '#347deb', dark: '#458de6' },
					'--color-bdg': { light: '#9ca3af', dark: '#363C45' },
					'--color-synth-body': { light: '#ffffff', dark: '#090A0B' },
					'--color-b-900': { light: '#0a0a0a', dark: '#090A0B' },
					'--color-b-800': { light: '#171717', dark: '#121417' },
					'--color-b-700': { light: '#262626', dark: '#1B1E22' },
					'--color-b-600': { light: '#404040', dark: '#24282E' },
					'--color-b-500': { light: '#525252', dark: '#090A0B' },
					'--color-b-400': { light: '#737373', dark: '#2D3239' },
					'--color-b-300': { light: '#a3a3a3', dark: '#363C45' },
					'--color-b-200': { light: '#d4d4d4', dark: '#48505B' },
					'--color-b-150': { light: '#64a7fa', dark: '#47A9FF' },
					'--color-b-100': { light: '#e5e5e5', dark: '#515A67' },
					'--color-b-50': { light: '#f5f5f5', dark: '#5A6472' },
					'--color-w-900': { light: '#F4F5F6', dark: '#0a0a0a' },
					'--color-w-800': { light: '#E8EAED', dark: '#171717' },
					'--color-w-700': { light: '#DDE0E4', dark: '#262626' },
					'--color-w-600': { light: '#D1D5DB', dark: '#404040' },
					'--color-w-500': { light: '#C6CBD2', dark: '#525252' },
					'--color-w-400': { light: '#BAC0C9', dark: '#737373' },
					'--color-w-300': { light: '#AFB6C0', dark: '#a3a3a3' },
					'--color-w-200': { light: '#A3ABB7', dark: '#d4d4d4' },
					'--color-w-100': { light: '#98A1AE', dark: '#e5e5e5' },
					'--color-w-50': { light: '#8D97A5', dark: '#f5f5f5' },
					'--color-chat-primary': { light: '#f3f4f6', dark: '#132947' },
					'--color-chat-secondary': { light: '#ffffff', dark: '#1C232B' },
					'--muted-foreground': { light: '0 0% 45.1%', dark: '0 0% 63.9%' },
					'--color-input-brdr': { light: '#d1d5db', dark: '#232930' },
					'--accent-foreground': { light: '0 0% 9%', dark: '0 0% 98%' },
					'--color-btn-bg': { light: 'rgba(20, 114, 255, 1)', dark: 'rgba(20, 114, 255, 1)' },
					'--color-guide-border': { light: 'rgba(209, 213, 219, 0.2)', dark: 'rgba(152, 161, 174, 0.08)' },
					'--cal-event-gentle-bg': { light: '#F9FAFB', dark: '#24282E' },
					'--cal-event-medium-bg': { light: '#E5E7EB', dark: '#1B1E22' },
					'--cal-event-strong-bg': { light: '#6B7280', dark: '#6B7280' },
					'--cal-event-gentle-fg': { light: '#6B7280', dark: '#9CA3AF' },
					'--cal-event-medium-fg': { light: '#FFFFFF', dark: '#E5E7EB' },
					'--cal-event-strong-fg': { light: '#FFFFFF', dark: '#FFFFFF' }
				};

				// Get user preference from localStorage (stored in .settings.dat by browser store)
				const storedConfig = localStorage.getItem('.settings.dat');
				let darkMode = 'auto'; // default

				if (storedConfig) {
					try {
						const config = JSON.parse(storedConfig);
						darkMode = config.darkMode || 'auto';
					} catch (e) {
						console.warn('Failed to parse config from localStorage');
					}
				}

				// Determine actual theme
				let theme = 'light';
				if (darkMode === 'dark') {
					theme = 'dark';
				} else if (darkMode === 'light') {
					theme = 'light';
				} else if (darkMode === 'auto' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
					theme = 'dark';
				}

				// Apply all CSS custom properties immediately
				Object.entries(colorMappings).forEach(function(entry) {
					const variable = entry[0];
					const colors = entry[1];
					const value = colors[theme];
					document.documentElement.style.setProperty(variable, value);
				});

				// Apply theme class immediately
				setTimeout(() => {
					if (theme === 'dark') {
						document.documentElement.classList.add('dark');
						document.documentElement.classList.remove('light');
						document.body.classList.add('dark');
						document.body.classList.remove('light');
					} else {
						document.documentElement.classList.add('light');
						document.documentElement.classList.remove('dark');
						document.body.classList.add('light');
						document.body.classList.remove('dark');
					}
				}, 1000);
			} catch (e) {
				console.warn('Failed to apply initial theme:', e);
			}
		})();
	</script>`}

	<title>{metaTags.title}</title>
	<meta name="title" content={metaTags.title} />
	<meta name="description" content={metaTags.description} />
	<!-- Block search engines on non-production domains -->
	{#if $page.url.hostname !== 'retainapp.ai' && $page.url.hostname !== 'www.retainapp.ai'}
		<meta name="robots" content="noindex, nofollow" />
	{/if}
	<!-- Canonical URL always points to production domain -->
	<link rel="canonical" href={`https://retainapp.ai${$page.url.pathname}`} />
	{#if metaTags['keywords']}
		<meta name="keywords" content={metaTags['keywords']} />
	{/if}
	{#if metaTags['author']}
		<meta name="author" content={metaTags['author']} />
	{/if}
	<meta property="og:type" content={metaTags['og:type']} />
	<meta property="og:url" content={metaTags['og:url']} />
	<meta property="og:title" content={metaTags['og:title']} />
	<meta property="og:description" content={metaTags['og:description']} />
	{#if metaTags['og:image']}
		<meta property="og:image" content={metaTags['og:image']} />
	{/if}
	{#if metaTags['og:image:width']}
		<meta property="og:image:width" content={metaTags['og:image:width']} />
	{/if}
	{#if metaTags['og:image:height']}
		<meta property="og:image:height" content={metaTags['og:image:height']} />
	{/if}
	{#if metaTags['og:site_name']}
		<meta property="og:site_name" content={metaTags['og:site_name']} />
	{/if}
	{#if metaTags['og:author']}
		<meta property="og:author" content={metaTags['og:author']} />
		<meta property="article:author" content={metaTags['article:author']} />
	{/if}
	{#if metaTags['og:published_time']}
		<meta property="og:published_time" content={metaTags['og:published_time']} />
		<meta property="article:published_time" content={metaTags['article:published_time']} />
	{/if}
	{#if metaTags['og:modified_time']}
		<meta property="og:modified_time" content={metaTags['og:modified_time']} />
		<meta property="article:modified_time" content={metaTags['article:modified_time']} />
	{/if}
	<meta property="twitter:card" content={metaTags['twitter:card']} />
	<meta property="twitter:url" content={metaTags['twitter:url']} />
	<meta property="twitter:title" content={metaTags['twitter:title']} />
	<meta property="twitter:description" content={metaTags['twitter:description']} />
	{#if metaTags['twitter:image']}
		<meta property="twitter:image" content={metaTags['twitter:image']} />
	{/if}

	<!-- Structured Data for Brand Recognition -->
	{@html `<script type="application/ld+json">
	{
		"@context": "https://schema.org",
		"@type": "Organization",
		"name": "Retain AI",
		"url": "https://retainapp.ai",
		"logo": "https://retainapp.ai/og-image.png",
		"sameAs": [
			"https://x.com/retain_ai",
			"https://linkedin.com/company/retainapp"
		],
		"description": "Retain AI is the ultimate productivity app that helps you organize, remember, and act on your information. Your second brain for seamless productivity."
	}
	</script>`}

	{@html `<script type="application/ld+json">
	{
		"@context": "https://schema.org",
		"@type": "WebSite",
		"name": "Retain AI",
		"url": "https://retainapp.ai",
		"description": "The only productivity app you will ever need",
		"publisher": {
			"@type": "Organization",
			"name": "Retain AI"
		}
	}
	</script>`}
</svelte:head>

<!-- Global navigation progress bar -->
<NavigationProgress />

<BaseMenuContext>
	<Tooltip.Provider>
		{#if shouldShowHeader}
			<LandingHeader
				signInHandler={async () => {
					try {
						const result = await signInWithCallbackUrl();
						console.log('Sign In Result', result);
					} catch (error) {
						console.error('Error signing in:', error);
					}
				}}
				signUpHandler={signUpWithCallbackUrl}
				{signOut}
				isBlog={pathname.startsWith('/blog')}
				enableApp={FEATURE_FLAGS['web-app']}
			/>
		{/if}

		{#if shouldShowMainPanes}
			<div class="h-screen w-full {shouldShowHeader ? 'pt-[var(--top-nav-height)]' : ''} ">
				<ProtectedPage {isLoading} hint="Please sign in to access your account">
					<!-- MainPanes.PaneGroup with direct pane children -->
					<MainPanes.PaneGroup isVisible={true} asSheet={isMobile}>
						{@render children?.()}
					</MainPanes.PaneGroup>
				</ProtectedPage>
			</div>
		{:else}
			<!-- Fallback content when main panes are not visible -->
			{@render children?.()}
		{/if}

		{#if shouldShowFooter}
			<LandingFooter
				signInHandler={signInWithCallbackUrl}
				signUpHandler={signUpWithCallbackUrl}
				isMinimal={pathname.startsWith('/blog') || pathname.startsWith('/help')}
			/>
		{/if}

		{#if mounted}
			<div class="fixed bottom-0 right-0 z-50">
				<Toaster />
			</div>
		{/if}

		{#if constants.IS_DEBUG}
			<DebugPanel />
		{/if}

		<!-- Download Desktop App Modal -->
		<DownloadDesktopAppModal
			open={$downloadModalStore.open}
			featureName={$downloadModalStore.featureName}
			onOpenChange={(isOpen) => {
				if (!isOpen) downloadModalStore.close();
			}}
		/>
		<ExportToLinearDialog
			open={$exportLinearDialogStore.open}
			taskIds={$exportLinearDialogStore.taskIds}
			memoryId={$exportLinearDialogStore.memoryId}
			onClose={closeExportToLinearDialog}
		/>

		{#if typeof import.meta.env !== 'undefined' && import.meta.env.MODE === 'production' && !pathname.startsWith('/app')}
			<GoogleAnalytics properties={['G-P086B5QL6N']} />
		{/if}
	</Tooltip.Provider>
</BaseMenuContext>
